import { useState, useCallback } from 'react'
import type { ChatMessage, ChatSuggestion } from '../../shared/types/chat'
import * as api from './api'
import { mapApiMessagesToChat } from './utils'
import { useAsyncData } from '../../shared/hooks/useAsyncData'

const EMPTY_MESSAGES: ChatMessage[] = []

// Personalized suggestion chips — derived from cortex/activity context
// In production, the API generates these from recent activity + memory.
// For the prototype, we simulate context-aware suggestions.
const SUGGESTIONS: ChatSuggestion[] = [
  { id: 'sug-001', text: 'Catch me up', icon: 'Sun', category: 'catchMeUp' },
]

interface ChatInitData {
  messages: ChatMessage[]
  suggestions: ChatSuggestion[]
  conversationId: string | null
}

async function loadChatData(): Promise<ChatInitData> {
  // Load real conversation history from API
  const convResponse = await api.getConversations()
  const activeConv = convResponse.conversations[0]

  if (!activeConv) {
    return { messages: [], suggestions: SUGGESTIONS, conversationId: null }
  }

  const msgResponse = await api.getMessages(activeConv.id)
  const messages = mapApiMessagesToChat(msgResponse.messages)

  return { messages, suggestions: SUGGESTIONS, conversationId: activeConv.id }
}

export interface ChatLoaderResult {
  messages: ChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  suggestions: ChatSuggestion[]
  conversationId: string | null
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>
  loading: boolean
  error: string | null
  refresh: () => Promise<ChatMessage[]>
}

export function useChatLoader(): ChatLoaderResult {
  const { data, loading, error } = useAsyncData(useCallback(() => loadChatData(), []))

  // Mutable state seeded from the loaded data, with local overrides
  const [localMessages, setLocalMessages] = useState<ChatMessage[] | null>(null)
  const [localConversationId, setLocalConversationId] = useState<string | null | undefined>(undefined)

  const messages = localMessages ?? data?.messages ?? EMPTY_MESSAGES
  const suggestions = data?.suggestions ?? SUGGESTIONS
  const conversationId = localConversationId !== undefined ? localConversationId : (data?.conversationId ?? null)

  // Wrap setMessages to switch from server data to local state on first mutation
  const setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>> = useCallback(
    (action) => {
      setLocalMessages(prev => {
        const current = prev ?? data?.messages ?? []
        return typeof action === 'function' ? action(current) : action
      })
    },
    [data?.messages],
  )

  const setConversationId: React.Dispatch<React.SetStateAction<string | null>> = useCallback(
    (action) => {
      setLocalConversationId(prev => {
        const current = prev !== undefined ? prev : (data?.conversationId ?? null)
        return typeof action === 'function' ? action(current) : action
      })
    },
    [data?.conversationId],
  )

  const refresh = useCallback(async (): Promise<ChatMessage[]> => {
    const convId = localConversationId !== undefined ? localConversationId : (data?.conversationId ?? null)
    if (!convId) return []
    try {
      const msgResponse = await api.getMessages(convId)
      const fresh = mapApiMessagesToChat(msgResponse.messages)
      setLocalMessages(fresh)
      return fresh
    } catch {
      return []
    }
  }, [localConversationId, data?.conversationId])

  return { messages, setMessages, suggestions, conversationId, setConversationId, loading, error, refresh }
}
